import React, { useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import projectConfig from '../assets/configs/projectConfig';
import Seo from '../components/Seo';
import { useLang } from '../utils/i18n';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [lang] = useLang();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const project = projectConfig.find((p) => p.slug === slug);

  if (!project) {
    return (
      <Container maxWidth="md" sx={{ py: 14, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
          Project not found
        </Typography>
        <Button component={RouterLink} to="/projects" startIcon={<ArrowBackIcon />} color="primary">
          Back to Projects
        </Button>
      </Container>
    );
  }

  const categoryArr = Array.isArray(project.category) ? project.category : [project.category];
  const categoryLabels = categoryArr
    .filter(Boolean)
    .map((c) => (typeof c === 'string' ? c : c[lang] || c.en));

  const paragraphs =
    project.longDescription_i18n?.[lang] ||
    project.longDescription_i18n?.en ||
    [project.description_i18n?.[lang] || project.description_i18n?.en || ''];

  const highlights = project.highlights_i18n?.[lang] || project.highlights_i18n?.en || [];
  const detailLinks = project.detailLinks || [];

  const backLabel = lang === 'es' ? 'Volver a Proyectos' : 'Back to Projects';
  const highlightsLabel = lang === 'es' ? 'Puntos clave' : 'Highlights';
  const resourcesLabel = lang === 'es' ? 'Recursos' : 'Resources';

  return (
    <>
      <Seo title={`${project.title} — Martín Antúnez`} />

      {/* Hero image with gradient fade-out at the bottom */}
      {project.image && (
        <Box sx={{ width: '100%', maxHeight: { xs: 220, md: 380 }, overflow: 'hidden', position: 'relative' }}>
          <Box
            component="img"
            src={project.image}
            alt={project.title}
            sx={{
              width: '100%',
              height: { xs: 220, md: 380 },
              objectFit: 'cover',
              display: 'block',
              filter: 'brightness(0.55) saturate(0.9)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(6,12,20,0.1) 30%, #060c14 100%)',
            }}
          />
        </Box>
      )}

      <Container maxWidth="md" sx={{ pb: { xs: 8, md: 12 }, mt: project.image ? -4 : 6 }}>
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>

          {/* Back button */}
          <Button
            component={RouterLink}
            to="/projects"
            startIcon={<ArrowBackIcon />}
            sx={{
              mb: 5,
              color: 'text.secondary',
              fontWeight: 600,
              '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
            }}
          >
            {backLabel}
          </Button>

          {/* Category chips */}
          {categoryLabels.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {categoryLabels.map((label) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  sx={(th) => ({
                    bgcolor: alpha(th.palette.primary.main, 0.1),
                    color: th.palette.primary.main,
                    border: `1px solid ${alpha(th.palette.primary.main, 0.3)}`,
                    fontWeight: 700,
                    fontSize: 12,
                  })}
                />
              ))}
            </Stack>
          )}

          {/* Title */}
          <Typography
            variant="h2"
            sx={{ fontWeight: 900, letterSpacing: -1.2, lineHeight: 1.1, mb: 6 }}
          >
            {project.title_i18n?.[lang] || project.title}
          </Typography>

          {/* Long description — mixed paragraphs and images */}
          <Stack spacing={4} sx={{ mb: 7 }}>
            {paragraphs.map((item, i) => {
              if (typeof item === 'string') {
                return (
                  <Typography
                    key={i}
                    variant="body1"
                    sx={{ color: 'text.secondary', lineHeight: 1.85, fontSize: { xs: 16, md: 17 } }}
                  >
                    {item}
                  </Typography>
                );
              }
              if (item?.type === 'table') {
                const headers = item.headers?.[lang] || item.headers?.en || [];
                const rows = item.rows?.[lang] || item.rows?.en || [];
                const caption = item.caption?.[lang] || item.caption?.en;
                return (
                  <Box key={i}>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, bgcolor: 'background.paper' }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'rgba(6,182,212,0.06)' }}>
                            {headers.map((h, hi) => (
                              <TableCell
                                key={hi}
                                sx={{ fontWeight: 800, color: '#7dd3fc', borderColor: 'rgba(255,255,255,0.07)', fontSize: 13, whiteSpace: 'nowrap' }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row, ri) => (
                            <TableRow
                              key={ri}
                              sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
                            >
                              {row.map((cell, ci) => (
                                <TableCell
                                  key={ci}
                                  sx={{ color: ci === 0 ? 'text.primary' : 'text.secondary', borderColor: 'rgba(255,255,255,0.07)', fontSize: 14, fontWeight: ci === 0 ? 700 : 400 }}
                                >
                                  {cell}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {caption && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'center', opacity: 0.7 }}>
                        {caption}
                      </Typography>
                    )}
                  </Box>
                );
              }
              if (item?.type === 'image-grid') {
                const imgBox = (img, height) => (
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden', p: 1 }}>
                      <Box component="img" src={img.src} alt={img.alt || ''} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                    </Box>
                    {img.caption && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', textAlign: 'center', opacity: 0.65, fontSize: '0.65rem' }}>
                        {img.caption}
                      </Typography>
                    )}
                  </Box>
                );

                if (item.layout === 'left-stack-right') {
                  const [left1, left2, right] = item.images;
                  return (
                    <Box key={i}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Stack spacing={2}>
                            {imgBox(left1, 190)}
                            {imgBox(left2, 190)}
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {imgBox(right, 398)}
                        </Grid>
                      </Grid>
                    </Box>
                  );
                }

                return (
                  <Box key={i}>
                    <Grid container spacing={2}>
                      {item.images.map((img, j) => (
                        <Grid item xs={12} sm={6} key={j}>
                          {imgBox(img, 220)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              }
              if (item?.type === 'image') {
                return (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={item.src}
                      alt={item.alt || ''}
                      sx={{
                        width: item.wide ? '100%' : { xs: '100%', md: '55%' },
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.08)',
                        display: 'inline-block',
                      }}
                    />
                    {item.caption && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'center', opacity: 0.7 }}
                      >
                        {item.caption}
                      </Typography>
                    )}
                  </Box>
                );
              }
              return null;
            })}
          </Stack>

          {/* Highlights */}
          {highlights.length > 0 && (
            <>
              <Divider sx={{ mb: 5 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mb: 3 }}>
                {highlightsLabel}
              </Typography>
              <Stack spacing={2} sx={{ mb: 7 }}>
                {highlights.map((h, i) => (
                  <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                    <CheckCircleOutlineIcon
                      sx={{ color: 'primary.main', fontSize: 20, mt: 0.2, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: 15 }}
                    >
                      {h}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}

          {/* Links */}
          {detailLinks.length > 0 && (
            <>
              <Divider sx={{ mb: 5 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mb: 3 }}>
                {resourcesLabel}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {detailLinks.map((link) => (
                  <Button
                    key={link.label}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={
                      <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { fontSize: 18 } }}>
                        {link.icon}
                      </Box>
                    }
                    sx={{
                      borderColor: 'rgba(255,255,255,0.14)',
                      color: 'text.primary',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'transparent' },
                    }}
                  >
                    {lang === 'es' ? link.labelEs : link.label}
                  </Button>
                ))}
              </Stack>
            </>
          )}

        </motion.div>
      </Container>
    </>
  );
}
